/**
 * Search allowance client (U5): mints a short-lived allowance from the
 * exchange endpoint after an invisible reCAPTCHA v3 pass, holds the token
 * in memory only (never storage — the fleet harvests anything persisted
 * or page-embedded), and renders the v2 checkbox fallback when the server
 * asks for a challenge in enforce mode.
 *
 * Design rules carried from the plan:
 * - Search calls AWAIT the in-flight mint (resolving null at the 5s
 *   script ceiling) instead of racing it — the mount-fired Find Similar
 *   search must not arrive headerless just because the exchange was slow.
 * - When the exchange failed or timed out, searches carry a fixed marker
 *   header instead, so log mode can split "our app, blocked script" from
 *   "no app at all".
 * - challenge_required is swallowed in log mode (the measurement window
 *   stays invisible); the checkbox renders only in enforce mode.
 * - Mode `off` short-circuits everything: no script injected, no headers.
 */

const SCRIPT_TIMEOUT_MS = 5000;
// Ceiling on the WHOLE mint (script + execute + exchange), not just the
// script load: a privacy extension can let api.js load and then stall the
// challenge frame, leaving execute() pending forever — and searches await
// the mint, so a hung mint must never hang searching.
const MINT_TIMEOUT_MS = 8000;
// After a negative verdict (low score, budget, failure), don't re-mint on
// every search — each mint bills a reCAPTCHA assessment and charges the
// per-IP mint limiter; ordinary clicking must not drain either.
const NEGATIVE_COOLDOWN_MS = 60000;

const state = {
    config: null,      // { mode, siteKey, scriptHost, v2SiteKey }
    token: null,       // current allowance token, memory only
    minting: null,     // in-flight mint promise (deduped)
    unavailable: false, // last mint attempt failed / script blocked
    challenged: false,  // server answered challenge_required
    lastNegativeAt: 0,  // timestamp of the last non-ok mint outcome
    challengeShown: false,
};

function config() {
    if (state.config) return state.config;

    const mount = document.getElementById('image-search');
    const d = (mount && mount.dataset) || {};

    state.config = {
        mode: d.captchaMode || 'off',
        siteKey: d.captchaSiteKey || '',
        scriptHost: d.captchaScriptHost || 'www.google.com',
        v2SiteKey: d.captchaV2SiteKey || '',
    };

    return state.config;
}

function active() {
    const c = config();
    return c.mode !== 'off' && c.siteKey !== '';
}

let scriptPromise = null;

/**
 * Inject api.js once (the v3 ?render= build also carries grecaptcha.render
 * for the v2 widget — one script serves both). Resolves grecaptcha or
 * null on timeout/failure; never rejects.
 */
function loadScript() {
    // Fresh check FIRST, every call: a script that finished loading after
    // an earlier timeout must be picked up — trusting only the memo would
    // leave a resolved-null promise poisoning the captcha for the whole
    // page session after one slow load.
    if (window.grecaptcha && window.grecaptcha.execute) {
        return Promise.resolve(window.grecaptcha);
    }

    if (scriptPromise) return scriptPromise;

    scriptPromise = new Promise((resolve) => {
        const c = config();
        const fail = () => {
            // Reset the memo so a later mint retries a fresh tag instead
            // of replaying this dead promise forever. Retry frequency is
            // already bounded by the negative-verdict cooldown.
            scriptPromise = null;
            resolve(null);
        };
        const timer = setTimeout(fail, SCRIPT_TIMEOUT_MS);
        const tag = document.createElement('script');
        tag.src = 'https://' + c.scriptHost + '/recaptcha/api.js?render=' + encodeURIComponent(c.siteKey);
        tag.async = true;
        tag.onload = () => {
            clearTimeout(timer);
            if (window.grecaptcha && window.grecaptcha.ready) {
                window.grecaptcha.ready(() => resolve(window.grecaptcha));
            } else {
                fail();
            }
        };
        tag.onerror = () => {
            clearTimeout(timer);
            fail();
        };
        document.head.appendChild(tag);
    });

    return scriptPromise;
}

async function exchange(payload) {
    const response = await fetch('/api/search-allowance', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-CSRF-TOKEN': typeof csrf_token !== 'undefined' ? csrf_token : '',
        },
        body: JSON.stringify(payload),
    });

    if (!response.ok) {
        return { code: 'error', status: response.status };
    }

    return response.json().catch(() => ({ code: 'error' }));
}

// The two state shapes every mint outcome resolves to — named so the
// field pairing can never drift between branches again (a missing
// lastNegativeAt on one branch silently disabled the cooldown for it).
function markNegative() {
    state.unavailable = true;
    state.lastNegativeAt = Date.now();
}

function acceptToken(token) {
    state.token = token;
    state.unavailable = false;
    state.challenged = false;
    state.lastNegativeAt = 0;
}

async function mintOnce() {
    const grecaptcha = await loadScript();

    if (!grecaptcha) {
        // Blocked/failed script is the most common real-world negative —
        // it engages the cooldown like every other failure, or every
        // search would re-inject a script tag and re-wait the timeout.
        markNegative();
        return null;
    }

    let captchaToken = null;

    try {
        captchaToken = await grecaptcha.execute(config().siteKey, { action: 'search_allowance' });
    } catch (e) {
        markNegative();
        return null;
    }

    let body;

    try {
        body = await exchange({ token: captchaToken });
    } catch (e) {
        markNegative(); // network failure — server policy decides
        return null;
    }

    if (body && body.code === 'ok' && body.token) {
        acceptToken(body.token);
        return body.token;
    }

    if (body && body.code === 'challenge_required') {
        // Log mode: swallow — the window must stay invisible to real
        // users. Enforce mode: the checkbox is the path forward. Either
        // way the CHALLENGED state is remembered so searches carry the
        // marker (a challenged human is not fleet traffic) and so we
        // don't re-bill an assessment on every subsequent search.
        if (config().mode === 'enforce') {
            showChallenge();
        }

        state.unavailable = false; // the exchange worked; we just scored low
        state.challenged = true;
        state.lastNegativeAt = Date.now();
        return null;
    }

    markNegative();
    return null;
}

function mint() {
    if (!state.minting) {
        state.minting = mintOnce().finally(() => {
            state.minting = null;
        });
    }

    // The caller-facing promise is deadlined; the mint itself keeps
    // running in the background and a late success still lands the
    // token for the NEXT search — but a hung mint never hangs this one.
    // The timer is cleared once the mint settles: an uncancelled timer
    // would fire after a successful fast mint and flip `unavailable`
    // back on, mislabeling later searches as script-blocked casualties.
    const minting = state.minting;
    let deadlineTimer = null;
    const deadline = new Promise((resolve) => {
        deadlineTimer = setTimeout(() => {
            state.unavailable = true;
            resolve(null);
        }, MINT_TIMEOUT_MS);
    });

    minting.finally(() => clearTimeout(deadlineTimer));

    return Promise.race([minting, deadline]);
}

function inNegativeCooldown() {
    return state.lastNegativeAt !== 0
        && (Date.now() - state.lastNegativeAt) < NEGATIVE_COOLDOWN_MS;
}

/**
 * The token to send, awaiting any in-flight mint. Starts a mint if none
 * has run yet (so the first caller and prime() share one exchange). A
 * recent negative verdict short-circuits instead of re-billing an
 * assessment per search.
 */
async function ready() {
    if (!active()) return null;
    if (state.token) return state.token;
    if (inNegativeCooldown()) return null;

    return mint();
}

/** Drop the current token and mint a fresh one (silent retry path). */
async function refresh() {
    if (!active()) return null;
    if (inNegativeCooldown()) return null;
    state.token = null;

    return mint();
}

/** Kick off script load + mint at app mount, in parallel with URL parsing. */
function prime() {
    if (active()) ready();
}

/**
 * Headers for a search call. Exactly one of the two headers is attached:
 * the allowance itself, or the script-blocked marker that lets the
 * would-refuse logs separate casualties from the fleet.
 */
async function headers(forceRefresh) {
    if (!active()) return {};

    const token = forceRefresh ? await refresh() : await ready();

    if (token) return { 'X-Search-Allowance': token };
    // Both markers say "this is our app, not a bare API caller" — the
    // would-refuse logs use them to keep challenged and script-blocked
    // humans out of the no-mint-attempt (fleet) bucket.
    if (state.challenged) return { 'X-Search-Allowance-State': 'challenged' };
    if (state.unavailable) return { 'X-Search-Allowance-State': 'unavailable' };

    return {};
}

const RETRYABLE = ['allowance_missing', 'allowance_expired', 'allowance_exhausted'];

/**
 * Should a refused search be silently retried once? Only on the three
 * allowance codes (never 429/5xx — a throttled or failing mint must not
 * loop), and for the missing code only when the script is actually
 * available now (its point is the slow-loading-script race).
 */
function shouldRetry(error) {
    if (!active()) return false;

    const response = error && error.response;
    const code = response && response.status === 401 && response.data && response.data.code;

    if (RETRYABLE.indexOf(code) === -1) return false;
    if (code === 'allowance_missing' && !(window.grecaptcha && window.grecaptcha.execute)) return false;

    return true;
}

/**
 * Minimal v2 checkbox panel, vanilla DOM — NoCaptcha::display() can't run
 * inside the React tree, and the panel outlives any one component. Shown
 * only in enforce mode when the server answered challenge_required.
 */
function showChallenge() {
    if (state.challengeShown) return;

    const c = config();

    if (!c.v2SiteKey || !window.grecaptcha || !window.grecaptcha.render) return;

    state.challengeShown = true;

    const panel = document.createElement('div');
    panel.id = 'search-allowance-challenge';
    panel.setAttribute('style', [
        'position:fixed', 'bottom:24px', 'left:50%', 'transform:translateX(-50%)',
        'background:#fff', 'border:1px solid #d0d0d0', 'border-radius:8px',
        'box-shadow:0 4px 24px rgba(0,0,0,.18)', 'padding:16px', 'z-index:2147483000',
        'text-align:center', 'max-width:340px',
    ].join(';'));

    const label = document.createElement('p');
    label.textContent = 'Please verify you are human to keep searching.';
    label.setAttribute('style', 'margin:0 0 10px;font-size:14px;');
    panel.appendChild(label);

    const widget = document.createElement('div');
    panel.appendChild(widget);
    document.body.appendChild(panel);

    window.grecaptcha.render(widget, {
        sitekey: c.v2SiteKey,
        callback: async (v2Response) => {
            try {
                const body = await exchange({ v2_response: v2Response });

                if (body && body.code === 'ok' && body.token) {
                    acceptToken(body.token);
                }
            } catch (e) {
                // Network failure mid-solve: record it (mirrors
                // mintOnce()) instead of an unhandled rejection that
                // leaves the state pretending nothing happened.
                markNegative();
            } finally {
                panel.remove();
                state.challengeShown = false;
            }
        },
    });
}

export default { prime, headers, refresh, shouldRetry };
